import { useEffect, useSyncExternalStore } from 'react';

type Theme = 'light' | 'dark' | 'system';

// Module-level state so every useTheme() consumer reads the same value.
// Earlier this hook owned a per-component useState, which kept the
// ThemeToggle in sync (it owned the writer) but left passive readers
// like the brand <Logo /> stale — Tailwind dark: variants worked
// because they read the <html> class, but JS consumers did not.
let currentTheme: Theme = readStoredTheme();
const listeners = new Set<() => void>();

function readStoredTheme(): Theme {
  if (typeof localStorage === 'undefined') return 'system';
  const stored = localStorage.getItem('theme');
  if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  return 'system';
}

function getSystemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme) {
  const resolved = theme === 'system' ? getSystemTheme() : theme;
  document.documentElement.classList.toggle('dark', resolved === 'dark');
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function notify() {
  listeners.forEach((l) => l());
}

// Apply the stored theme to <html> as early as the module loads so we
// don't flash light-mode on a hard refresh in dark mode.
if (typeof document !== 'undefined') applyTheme(currentTheme);

export function useTheme() {
  const theme = useSyncExternalStore(
    subscribe,
    () => currentTheme,
    () => currentTheme,
  );

  // While in 'system' mode, react to OS-level theme changes so every
  // subscriber re-renders together.
  useEffect(() => {
    if (theme !== 'system') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => {
      applyTheme('system');
      notify();
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, [theme]);

  function setTheme(newTheme: Theme) {
    localStorage.setItem('theme', newTheme);
    currentTheme = newTheme;
    applyTheme(newTheme);
    notify();
  }

  function toggleTheme() {
    const resolved = theme === 'system' ? getSystemTheme() : theme;
    setTheme(resolved === 'dark' ? 'light' : 'dark');
  }

  const resolvedTheme = theme === 'system' ? getSystemTheme() : theme;

  return { theme, resolvedTheme, setTheme, toggleTheme };
}
